import { useState } from 'react';
import clsx from 'clsx';
import { LEVEL_GROUPS } from '@rc/engine';
import type { Channel, Discipline, Model } from '@rc/engine/types';
import SpendPerFteTable from './model/SpendPerFteTable';
import LevelWeightsMatrix from './model/LevelWeightsMatrix';
import CostPerRoleMatrix from './model/CostPerRoleMatrix';
import OffshorePctMatrix from './model/OffshorePctMatrix';
import BucketsHubsEditor from './model/BucketsHubsEditor';
import TimeConcentrationEditor from './model/TimeConcentrationEditor';
import ProfileFactorsEditor from './model/ProfileFactorsEditor';
import SavedPresetsEditor from './model/SavedPresetsEditor';
import CurrencyEditor from './model/CurrencyEditor';
import StaffPlanParserEditor from './model/StaffPlanParserEditor';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

type SectionId = 'scenario' | 'staff_plan_parser';

const SCENARIO_TABS: { id: ScenarioTabId; label: string }[] = [
  { id: 'disciplines', label: 'Disciplines' },
  { id: 'channels', label: 'Channels' },
  { id: 'levels', label: 'Levels' },
  { id: 'profile_factors', label: 'Profile factors' },
  { id: 'spend_per_fte', label: 'Spend per FTE' },
  { id: 'level_weights', label: 'Level weights' },
  { id: 'cost_per_role', label: 'Cost per role' },
  { id: 'offshore_pct', label: 'Hub locations %' },
  { id: 'saved_presets', label: 'Saved presets' },
  { id: 'buckets_hubs', label: 'Buckets & hubs' },
  { id: 'time_concentration', label: 'Time concentration' },
  { id: 'currency', label: 'Currency' },
];

const PARSER_TABS: { id: ParserTabId; label: string }[] = [{ id: 'skill', label: 'Parser skill' }];

type ScenarioTabId =
  | 'channels'
  | 'disciplines'
  | 'levels'
  | 'profile_factors'
  | 'spend_per_fte'
  | 'level_weights'
  | 'cost_per_role'
  | 'offshore_pct'
  | 'saved_presets'
  | 'buckets_hubs'
  | 'time_concentration'
  | 'currency';

type ParserTabId = 'skill';

export default function ModelEditor({ model, onChange }: Props) {
  const [section, setSection] = useState<SectionId>('scenario');
  const [scenarioTab, setScenarioTab] = useState<ScenarioTabId>('spend_per_fte');
  const [parserTab, setParserTab] = useState<ParserTabId>('skill');

  const tabs = section === 'scenario' ? SCENARIO_TABS : PARSER_TABS;
  const activeTab = section === 'scenario' ? scenarioTab : parserTab;

  return (
    <div className="card">
      <div className="border-b border-slate-200 px-4 pt-3 flex flex-wrap items-end gap-4">
        <div className="flex gap-1 pb-2">
          {(
            [
              { id: 'scenario' as const, label: 'Scenario' },
              { id: 'staff_plan_parser' as const, label: 'Staff Plan Parser' },
            ] as const
          ).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={clsx(
                'px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
                section === s.id
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 flex-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                if (section === 'scenario') setScenarioTab(t.id as ScenarioTabId);
                else setParserTab(t.id as ParserTabId);
              }}
              className={clsx(
                'px-3.5 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px',
                activeTab === t.id
                  ? 'text-brand-purple-700 border-brand-purple-500 bg-brand-purple-50/40'
                  : 'text-slate-500 border-transparent hover:text-slate-800',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {section === 'scenario' && (
          <>
            {scenarioTab === 'channels' && <ChannelsTab model={model} onChange={onChange} />}
            {scenarioTab === 'disciplines' && <DisciplinesTab model={model} onChange={onChange} />}
            {scenarioTab === 'levels' && <LevelsTab />}
            {scenarioTab === 'profile_factors' && (
              <ProfileFactorsEditor model={model} onChange={onChange} />
            )}
            {scenarioTab === 'spend_per_fte' && <SpendPerFteTable model={model} onChange={onChange} />}
            {scenarioTab === 'level_weights' && (
              <LevelWeightsMatrix model={model} onChange={onChange} />
            )}
            {scenarioTab === 'cost_per_role' && (
              <CostPerRoleMatrix model={model} onChange={onChange} />
            )}
            {scenarioTab === 'offshore_pct' && <OffshorePctMatrix model={model} onChange={onChange} />}
            {scenarioTab === 'saved_presets' && (
              <SavedPresetsEditor model={model} onChange={onChange} />
            )}
            {scenarioTab === 'buckets_hubs' && <BucketsHubsEditor model={model} onChange={onChange} />}
            {scenarioTab === 'time_concentration' && (
              <TimeConcentrationEditor model={model} onChange={onChange} />
            )}
            {scenarioTab === 'currency' && <CurrencyEditor model={model} onChange={onChange} />}
          </>
        )}
        {section === 'staff_plan_parser' && (
          <StaffPlanParserEditor model={model} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

function ChannelsTab({ model, onChange }: Props) {
  const update = (channels: Channel[]) => onChange({ ...model, channels });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left py-2 pr-2">Label</th>
            <th className="text-left py-2 pr-2">ID</th>
            <th className="text-left py-2 pr-2">Category</th>
            <th className="text-center py-2 pr-2">AdOps?</th>
            <th className="text-center py-2 pr-2">Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {model.channels.map((c, i) => (
            <tr key={c.id} className="border-t border-slate-100">
              <td className="py-1 pr-2">
                <input
                  className="input py-1 px-2"
                  value={c.label}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, label: e.target.value };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-xs font-mono text-slate-500">{c.id}</td>
              <td className="py-1 pr-2">
                <select
                  className="input py-1 px-2"
                  value={c.category}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, category: e.target.value as Channel['category'] };
                    update(next);
                  }}
                >
                  <option value="traditional">Traditional</option>
                  <option value="addressable">Addressable</option>
                </select>
              </td>
              <td className="py-1 pr-2 text-center">
                <input
                  type="checkbox"
                  checked={!!c.adopsApplicable}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, adopsApplicable: e.target.checked };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-center">
                <input
                  type="checkbox"
                  checked={c.active !== false}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, active: e.target.checked };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 text-right">
                <button
                  className="btn-ghost text-xs"
                  onClick={() => update(model.channels.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-3">
        Channel mix for new scenarios is seeded from <strong>channelMixPresets</strong> or split
        evenly; per-channel allocation lives on each scenario, not the model.
      </p>
      <button
        className="btn-secondary mt-3"
        onClick={() => {
          const id = prompt('New channel id (slug)');
          if (!id) return;
          update([
            ...model.channels,
            {
              id,
              label: id,
              category: 'addressable',
              active: true,
            },
          ]);
        }}
      >
        + Add channel
      </button>
    </div>
  );
}

function DisciplinesTab({ model, onChange }: Props) {
  const update = (disciplines: Discipline[]) => onChange({ ...model, disciplines });
  const setField = (i: number, patch: Partial<Discipline>) => {
    const next = [...model.disciplines];
    next[i] = { ...next[i], ...patch };
    update(next);
  };
  return (
    <div className="space-y-3">
      {model.disciplines.map((d, i) => (
        <div key={d.id} className="border border-slate-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label">Label</label>
              <input
                className="input"
                value={d.label}
                onChange={(e) => setField(i, { label: e.target.value })}
              />
            </div>
            <div>
              <label className="label">ID</label>
              <input className="input bg-slate-50 text-slate-500" value={d.id} disabled />
            </div>
            <div>
              <label className="label">Active</label>
              <input
                type="checkbox"
                checked={d.active !== false}
                onChange={(e) => setField(i, { active: e.target.checked })}
              />
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Level mix is driven by <strong>Level weights</strong>.
          </p>
        </div>
      ))}
    </div>
  );
}

function LevelsTab() {
  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-xs text-slate-500">
        Job levels are fixed across every model. Rates, weights and staff plans are stored for
        each exact level (L1–L8) so they stay precise; everywhere else the app shows these four
        groups.
      </p>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left py-2 pr-2">Level group</th>
            <th className="text-left py-2 pr-2">Levels</th>
          </tr>
        </thead>
        <tbody>
          {LEVEL_GROUPS.map((g) => (
            <tr key={g.id} className="border-t border-slate-100">
              <td className="py-2 pr-2 font-medium text-slate-700">{g.label}</td>
              <td className="py-2 pr-2 text-xs font-mono text-slate-500">
                {g.levelIds.join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

